import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { API_URL } from '../lib/utils';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from './ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from './ui/select';
import { BellPlus } from 'lucide-react';

// Map the Jobs page filter object into a saved-search payload
export const filtersToPayload = (filters) => ({
  search: filters.search || null,
  location: filters.location || null,
  license_types: filters.license && filters.license !== 'all' ? [filters.license] : [],
  job_types: filters.jobType && filters.jobType !== 'all' ? [filters.jobType] : [],
  remote_type: filters.isRemote ? 'remote' : null,
});

const summarize = (filters) => {
  const chips = [];
  if (filters.search) chips.push(`"${filters.search}"`);
  if (filters.license && filters.license !== 'all') chips.push(filters.license);
  if (filters.jobType && filters.jobType !== 'all') chips.push(filters.jobType);
  if (filters.location) chips.push(filters.location);
  if (filters.isRemote) chips.push('Remote');
  return chips;
};

const SaveSearchButton = ({ filters }) => {
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [frequency, setFrequency] = useState('daily');
  const [saving, setSaving] = useState(false);

  const chips = summarize(filters);

  const handleOpen = () => {
    if (!isAuthenticated) {
      toast.info('Sign in or create a free account to save searches and get job alerts.');
      navigate('/login');
      return;
    }
    const auto = chips.length ? chips.join(' · ') : 'All social work jobs';
    setName(auto.slice(0, 60));
    setOpen(true);
  };

  const save = async () => {
    if (!name.trim()) { toast.error('Please name your search.'); return; }
    setSaving(true);
    try {
      await axios.post(`${API_URL}/saved-searches`, {
        name: name.trim(), frequency, ...filtersToPayload(filters),
      });
      toast.success('Search saved! Manage alerts in your dashboard.');
      setOpen(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not save search.');
    } finally { setSaving(false); }
  };

  return (
    <>
      <Button
        type="button"
        variant="outline"
        className="border-secondary text-secondary hover:bg-secondary hover:text-white"
        onClick={handleOpen}
        data-testid="save-search-btn"
      >
        <BellPlus className="w-4 h-4 mr-2" /> Save This Search
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-white max-w-md">
          <DialogHeader><DialogTitle>Save Search &amp; Get Alerts</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-xs text-gray-500 mb-1 block">Search name</Label>
              <Input value={name} onChange={(e) => setName(e.target.value)} data-testid="save-search-name" placeholder="e.g. Remote LCSW in Texas" />
            </div>
            <div>
              <Label className="text-xs text-gray-500 mb-1 block">Alert frequency</Label>
              <Select value={frequency} onValueChange={setFrequency}>
                <SelectTrigger data-testid="save-search-frequency"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="immediate">Immediately when a matching job is posted</SelectItem>
                  <SelectItem value="daily">Daily summary</SelectItem>
                  <SelectItem value="weekly">Weekly summary</SelectItem>
                  <SelectItem value="off">Alerts turned off</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs text-gray-500 mb-2 block">Filters applied</Label>
              {chips.length ? (
                <div className="flex flex-wrap gap-2" data-testid="save-search-filters">
                  {chips.map((c) => <Badge key={c} className="bg-primary/10 text-primary">{c}</Badge>)}
                </div>
              ) : (
                <p className="text-sm text-gray-500">No filters — you'll be alerted about all new social work jobs.</p>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button className="bg-primary text-white" onClick={save} disabled={saving} data-testid="save-search-confirm">
              {saving ? 'Saving…' : 'Save & Create Alert'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default SaveSearchButton;
